/**
 * Custom Next.js App component.
 * -----------------------------------------
 * This file initializes:
 *  - TRPC client integration
 *  - Context providers (global stores)
 *  - Initial data loading wrapper
 *  - Global SEO config via next-seo
 *  - Global layout (Header, Footer, SpotifyFooter)
 *
 * Compatible with:
 *  - Node 18–24
 *  - Next.js 12–14 (Pages Router)
 */

import { trpc } from "components/_app/trpc";
import { ContextProviders } from "components/_stores/_context-providers";
import { LoadInitialData } from "components/_stores/_load-initial-data";

import { Footer } from "components/layout/footer";
import SpotifyFooter from "components/layout/SpotifyFooter";
import { Header } from "components/layout/header";

import { SEO } from "content/seo";
import { DefaultSeo } from "next-seo";

import type { AppProps } from "next/app";
import { useRouter } from "next/router";

import { FC, PropsWithChildren, useEffect, useState } from "react";

import "styles/tailwind.css";

/**
 * Wrapper that loads context providers + initial data.
 */
const Loaders: FC<PropsWithChildren> = ({ children }) => {
  return (
    <ContextProviders>
      <LoadInitialData>{children}</LoadInitialData>
    </ContextProviders>
  );
};

/**
 * Main application component (Next.js Pages Router).
 * Handles:
 *  - SEO injection
 *  - Loading guard for first client render
 *  - Global layout wrappers
 */
const App = ({ pageProps, Component }: AppProps) => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);

  /**
   * Client-side init + devtools console banner.
   */
  useEffect(() => {
    setLoading(false);

    if (process.env.NODE_ENV !== "development") {
      console.log(
        "%cHEY YOU! I see you sneaking in my code. This Page is custom built by Prabhjyoth Mattummal. I mainly used Next.js, TailwindCSS, Typescript, Vercel, and TRPC as the main tech here. It's something I've worked hard on so please do not copy it directly. LEARN FROM IT AND MAKE IT YOUR OWN. Questions? Just drop me an email at i@mattumm.al! You can find the repo learning purposes for the site here: https://github.com/mattummal/portfolio",
        "background: rgb(0,0,0);color: #fafafa;font-size: 24px;font-weight: bold;padding: 25px 10px;text-align: center;text-shadow: 2px 2px 0 rgba(45, 45, 45);"
      );
    }
  }, []);

  /** Prevents hydration mismatches */
  if (loading) return <></>;

  return (
    <Loaders>
      <DefaultSeo
        {...SEO}
        canonical={`${SEO.url}${router.asPath}`}
        twitter={SEO.twitter}
        title={SEO.title}
        description={SEO.description}
        openGraph={SEO.openGraph}
      />

      <Header />

      <main className="min-h-screen print:!mx-auto print:!w-[1024px]">
        <Component {...pageProps} />
      </main>

      <SpotifyFooter />
      <Footer />
    </Loaders>
  );
};

/**
 * TRPC HOC must wrap the default export.
 */
export default trpc.withTRPC(App);
